import React, { ChangeEvent } from 'react';
import { useDrop } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';
import { Typography, Stack, Button } from '@mui/material';
import { CloudUploadOutlined } from '@mui/icons-material';
import { Card } from 'components/Card';
import { CreativeTypePreview } from 'components/CreativeTypePreview';
import { useCampaign } from 'hooks/useCampaign';
import { useIsMobile } from 'hooks/useIsMobile';
import { creativeAcceptMediaTypes, getDimensions } from 'utils';

export type CreativeUploaderProps = {
  onUpload: (file: File[]) => void;
  hasError?: boolean;
};

export const CreativeUploader = ({ onUpload, hasError }: CreativeUploaderProps) => {
  const { isMobile } = useIsMobile();
  const { getCreativeTypesQuery } = useCampaign();
  const [{ canDrop }, drop] = useDrop(() => ({
    accept: NativeTypes.FILE,
    drop: ({ files }: { files: FileList }) => onUpload(Array.from(files)),
    collect: (monitor) => ({ canDrop: monitor.canDrop() }),
  }));

  const { creativeTypes = [] } = getCreativeTypesQuery?.data ?? {};

  const title = isMobile ? 'Bilder / Videos auswählen' : 'Bilder / Videos hier ablegen oder auswählen';
  const borderColor = hasError ? 'error.dark' : 'primary.main';

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;

    if (!files?.length) {
      return;
    }

    onUpload(Array.from(files));

    // this will ensure that the file input is cleared after the file is uploaded
    e.currentTarget.value = '';
  };

  return (
    <Button
      ref={drop}
      size="large"
      disableRipple
      component="label"
      sx={{ display: 'block', p: 0, opacity: canDrop ? 0.4 : 1, textTransform: 'none' }}
    >
      <Card sx={{ borderStyle: 'dashed', border: 3, borderColor }}>
        <Stack alignItems="center">
          <CloudUploadOutlined sx={{ fontSize: 60, color: 'primary.main' }} />
          <Typography variant="h4" color="primary.main" mb={4}>
            {title}
          </Typography>
          <Stack
            direction="row"
            useFlexGap
            flexWrap={isMobile ? 'wrap' : 'nowrap'}
            width="100%"
            justifyContent="space-between"
            spacing={2}
          >
            {creativeTypes.map(({ displayName, width, height }) => (
              <CreativeTypePreview
                key={displayName}
                name={displayName}
                aspectRatio={width / height}
                dimensions={getDimensions(width, height)}
              />
            ))}
          </Stack>
        </Stack>
      </Card>
      <input
        hidden
        multiple
        type="file"
        onChange={onFileChange}
        accept={creativeAcceptMediaTypes}
        data-testid="fileUpload"
      />
    </Button>
  );
};
